import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Check,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { createLead } from "../lib/api";
import { compressImage } from "../lib/image";
import { STEPS, PROJECT_TYPES } from "./contact/constants";
import {
  StepIndicator,
  StepProjectType,
  StepSurface,
  StepDescription,
  StepSlots,
  StepPersonal,
  SuccessCard,
} from "./contact/steps";

const INITIAL_DATA = {
  projectType: "",
  surface: [80],
  message: "",
  slots: [{ day: "", hour: "" }],
  prenom: "",
  nom: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  zipCode: "",
};

let photoIdCounter = 0;

export default function Contact({ prefill }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [data, setData] = useState(INITIAL_DATA);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!prefill) return;
    setData((d) => ({ ...d, ...prefill }));
  }, [prefill]);

  const update = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const addSlot = () =>
    setData((d) => ({ ...d, slots: [...d.slots, { day: "", hour: "" }] }));
  const removeSlot = (idx) =>
    setData((d) => ({
      ...d,
      slots: d.slots.length === 1 ? d.slots : d.slots.filter((_, i) => i !== idx),
    }));
  const updateSlot = (idx, field, value) =>
    setData((d) => ({
      ...d,
      slots: d.slots.map((s, i) => (i === idx ? { ...s, [field]: value } : s)),
    }));

  const addPhotos = async (files) => {
    if (!files?.length) return;
    const arr = Array.from(files).slice(0, 6);
    try {
      const processed = await Promise.all(
        arr.map(async (file) => {
          if (file.size > 12 * 1024 * 1024) {
            throw new Error("Photo trop volumineuse (max 12 Mo)");
          }
          const dataUrl = await compressImage(file, {
            maxDim: 1600,
            quality: 0.85,
          });
          photoIdCounter += 1;
          return { id: `photo-${photoIdCounter}`, name: file.name, dataUrl };
        })
      );
      setPhotos((p) => [...p, ...processed].slice(0, 6));
    } catch (e) {
      toast.error(e.message || "Impossible d'ajouter cette photo.");
    }
  };
  const removePhoto = (idx) =>
    setPhotos((p) => p.filter((_, i) => i !== idx));

  const canGoNext = (() => {
    if (step === 1) return !!data.projectType;
    if (step === 2) return data.surface[0] >= 20;
    if (step === 3) return true;
    if (step === 4)
      return data.slots.some((s) => s.day && s.hour);
    if (step === 5)
      return (
        data.prenom.trim() &&
        data.nom.trim() &&
        data.phone.trim() &&
        data.email.trim() &&
        data.address.trim() &&
        data.city.trim() &&
        data.zipCode.trim()
      );
    return false;
  })();

  const goNext = () => {
    if (!canGoNext) return;
    if (step < STEPS) setStep(step + 1);
  };
  const goPrev = () => step > 1 && setStep(step - 1);

  const submit = async () => {
    if (!canGoNext) return;
    setSubmitting(true);
    try {
      const projectLabel =
        PROJECT_TYPES.find((p) => p.key === data.projectType)?.label ||
        data.projectType;
      const validSlots = data.slots
        .filter((s) => s.day && s.hour)
        .map((s) => `${s.day} à ${s.hour}`);
      await createLead({
        name: `${data.prenom.trim()} ${data.nom.trim()}`,
        phone: data.phone.trim(),
        email: data.email.trim(),
        city: data.city.trim(),
        zip_code: data.zipCode.trim(),
        address: data.address.trim(),
        surface: data.surface[0],
        message: data.message.trim(),
        project_type: projectLabel,
        appointment_slots: validSlots,
        custom_options: [
          `Type de projet : ${projectLabel}`,
          `Surface estimée : ${data.surface[0]} m²`,
          `Créneaux proposés : ${validSlots.join(" · ") || "—"}`,
          "Réservation de rendez-vous depuis le site GMC.",
        ],
        facade_photo_base64: photos[0]?.dataUrl || "",
        extra_photos: photos.slice(1).map((p) => p.dataUrl),
      });
      setSuccess(true);
    } catch (err) {
      toast.error(
        "Envoi impossible. Réessayez ou appelez-nous au 07 63 43 38 67."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setSuccess(false);
    setStep(1);
    setPhotos([]);
    setData(INITIAL_DATA);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 px-6 md:px-12 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <div className="eyebrow mb-3 inline-flex items-center gap-2">
            <CalendarDays size={12} />
            <span>Devis gratuit sous 24 h</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-3">
            Réservez votre rendez-vous
            <br />
            <em className="not-italic font-normal text-[#FF5A00]">
              pour un devis
            </em>
          </h2>
          <p className="text-neutral-400 text-sm md:text-base max-w-xl mx-auto">
            Choisissez vos disponibilités, décrivez votre projet et recevez une
            confirmation rapidement.
          </p>
        </div>

        {success ? (
          <SuccessCard onReset={resetAll} />
        ) : (
          <div className="glass-strong rounded-3xl p-6 md:p-10 relative">
            <StepIndicator step={step} total={STEPS} />

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="mt-8"
              >
                {step === 1 && (
                  <StepProjectType
                    value={data.projectType}
                    onChange={update("projectType")}
                  />
                )}
                {step === 2 && (
                  <StepSurface
                    value={data.surface}
                    onChange={update("surface")}
                  />
                )}
                {step === 3 && (
                  <StepDescription
                    message={data.message}
                    onMessage={update("message")}
                    photos={photos}
                    onAddPhotos={addPhotos}
                    onRemovePhoto={removePhoto}
                    fileRef={fileRef}
                  />
                )}
                {step === 4 && (
                  <StepSlots
                    slots={data.slots}
                    onUpdate={updateSlot}
                    onAdd={addSlot}
                    onRemove={removeSlot}
                  />
                )}
                {step === 5 && (
                  <StepPersonal data={data} update={update} />
                )}
              </motion.div>
            </AnimatePresence>

            {/* Nav buttons */}
            <div className="mt-8 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={goPrev}
                disabled={step === 1}
                data-testid="contact-prev"
                className="btn-ghost text-sm disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={14} /> Retour
              </button>
              {step < STEPS ? (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!canGoNext}
                  data-testid="contact-next"
                  className="btn-primary text-sm px-6 py-3 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continuer <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canGoNext || submitting}
                  data-testid="contact-submit"
                  className="btn-primary text-sm md:text-base px-6 py-3 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Envoi…
                    </>
                  ) : (
                    <>
                      <Check size={14} /> Envoyer ma demande de rendez-vous
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Bandeau téléphone urgence */}
        <div className="mt-10 text-center text-sm text-neutral-400">
          Vous préférez un contact direct ?{" "}
          <a
            href="tel:+33763433867"
            data-testid="contact-phone-link"
            className="inline-flex items-center gap-1.5 text-[#FF5A00] hover:underline"
          >
            <Phone size={13} /> 07 63 43 38 67
          </a>
        </div>
      </div>
    </section>
  );
}
